import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  useMediaQuery,
  Link,
} from "@mui/material";
import internationalSVG from "../../images/svg/international.svg";
import domesticSVG from "../../images/svg/domestic.svg";
import indiaSVG from "../../images/svg/indian-economy.svg";
import ukraineSVG from "../../images/svg/ukraine-economy.svg";
import packagingSVG from "../../images/svg/packaging.svg";
import BlobWrapper from "../utilities/BlobWrapper";

const tabLabels = [
  "International",
  "Domestic",
  "India Economy",
  "Ukraine Economy",
  "Packaging Services",
];

const tabImages = {
  International: internationalSVG,
  Domestic: domesticSVG,
  "India Economy": indiaSVG,
  "Ukraine Economy": ukraineSVG,
  "Packaging Services": packagingSVG,
};

const tabContent = (isMobile) => ({
  International: {
    title: "Global Shipping Solutions",
    description: isMobile ? (
      "Fast, affordable delivery via DHL & UPS. Simple and reliable overseas shipping."
    ) : (
      <>
        We offer fast, affordable international delivery through trusted
        carriers like <b>DHL</b> and <b>UPS</b>. As an authorized pick-up and
        drop-off location, we make overseas shipping simple and reliable.
      </>
    ),
  },
  Domestic: {
    title: "Reliable Canadian Deliveries",
    description: isMobile ? (
      "Ship across Canada with Purolator, UPS & more. Fast and secure."
    ) : (
      <>
        Ship across Canada with ease using services from <b>Purolator</b>,{" "}
        <b>UPS</b>, and more. Our domestic shipping is fast, secure, and
        accessible through convenient drop-off and pick-up services.
      </>
    ),
  },
  "India Economy": {
    title: "Affordable Shipping to India",
    description: isMobile ? (
      "Low-cost shipping to India with tracking included."
    ) : (
      <>
        Enjoy <b>low-cost shipping</b> to India with tracking included. We offer
        reliable delivery options at the <b>cheapest available rates</b>.
      </>
    ),
  },
  "Ukraine Economy": {
    title: "Shipping to Ukraine",
    description: isMobile ? (
      "Yes, we ship to Ukraine with Meest Canada by air & sea."
    ) : (
      <>
        Yes, we ship to Ukraine. <b>Meest Canada</b> continues to operate during
        the conflict, offering both <b>air and sea delivery</b> options.
      </>
    ),
  },
  "Packaging Services": {
    title: "Packaging That Protects",
    description: isMobile ? (
      "Boxes, bubble wrap & more to keep your shipments safe."
    ) : (
      <>
        Explore a wide range of <b>packaging materials</b>—from boxes to bubble
        wrap. Whether you're shipping fragile or bulky items, we’ve got you
        covered.
      </>
    ),
  },
});

const MultiTab = () => {
  const [value, setValue] = useState(0);
  const isMobile = useMediaQuery("(max-width: 900px)");

  const content = tabContent(isMobile);

  return (
    <Box
      sx={{
        position: "relative",
        background: "var(--gradient-blue)",
        color: "white",
        maxWidth: "70rem",
        mx: "auto",
        mt: "-10rem",
        px: { xs: 4, md: 10 },
        py: { xs: 3, md: 6 },
        borderRadius: 4,
        boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
        zIndex: 2,
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <DistributedBlobs />

      <Tabs
        value={value}
        onChange={(e, newVal) => setValue(newVal)}
        variant={isMobile ? "standard" : "fullWidth"}
        sx={{
          mb: 4,
          flexWrap: isMobile ? "wrap" : "nowrap",
          "& .MuiTabs-flexContainer": {
            flexWrap: isMobile ? "wrap" : "nowrap",
            justifyContent: isMobile ? "center" : "flex-start",
            gap: isMobile ? 1 : 0,
          },
          "& .MuiTabs-indicator": {
            display: isMobile ? "none" : "block",
            backgroundColor: "var(--yellow-main)",
            height: 4,
            borderRadius: 2,
          },
        }}
      >
        {tabLabels.map((label) => (
          <Tab
            key={label}
            label={label}
            sx={{
              py: { xs: 0.5, sm: 1 },
              px: { xs: 1.5, sm: 3 },
              color: "white",
              fontWeight: 500,
              fontSize: isMobile ? "1.2rem" : "1rem", // now larger for mobile
              textTransform: "none",
              "&.Mui-selected": { color: "white", fontWeight: 600 },
              "&:hover": { color: "var(--yellow-hover)" },
            }}
          />
        ))}
      </Tabs>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: { xs: 4, md: 6 },
          textAlign: { xs: "center", md: "left" },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: { xs: "14rem", md: "20rem" },
            mx: "auto",
            flexShrink: 0,
          }}
        >
          <BlobWrapper src={tabImages[tabLabels[value]]} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.5rem" },
              mb: 2,
              background: "linear-gradient(90deg, #fefefe, #ffffff)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {content[tabLabels[value]].title}
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              color: "var(--color-white)",
              maxWidth: 700,
              mb: 4,
              fontSize: isMobile ? "1.1rem" : "1rem",
              lineHeight: 1.7,
            }}
          >
            {content[tabLabels[value]].description}
          </Typography>

          <Button
            component={Link}
            href="https://skymartexpress.com"
            target="_blank"
            variant="outlined"
            sx={{
              border: "3px solid var(--yellow-main)",
              color: "var(--yellow-main)",
              fontWeight: 600,
              textTransform: "none",
              px: isMobile ? 5 : 4,
              py: isMobile ? 1.4 : 1.2,
              fontSize: isMobile ? "1.1rem" : "1rem",
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "rgba(253, 224, 71, 0.12)",
                borderColor: "var(--yellow-hover)",
                color: "var(--yellow-hover)",
              },
            }}
          >
            Learn More
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const DistributedBlobs = () => {
  const blobStyles = (top, left) => ({
    position: "absolute",
    top,
    left,
    opacity: 0.08,
    zIndex: 0,
  });

  const positions = [
    ["-15px", "-25px"],
    ["10%", "50%"],
    ["-20px", "90%"],
    ["calc(100% - 60px)", "15%"],
    ["calc(100% - 80px)", "50%"],
    ["calc(100% - 50px)", "80%"],
  ];

  return (
    <>
      {positions.map(([top, left], idx) => (
        <svg
          key={idx}
          style={blobStyles(top, left)}
          width="100"
          height="100"
          viewBox="0 0 100 100"
        >
          <circle cx="50" cy="50" r="50" fill="var(--blob-blue)" />
        </svg>
      ))}
    </>
  );
};

export default MultiTab;
